import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ORG_LOGOS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Menu, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/standards", label: "Standards" },
  { to: "/registry", label: "Public Registry" },
  { to: "/schools", label: "Approved Schools" },
  { to: "/verify", label: "Verify" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header
        data-testid="public-header"
        className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
            <div className="flex items-center gap-2">
              <img src={ORG_LOGOS.ACIS} alt="ACIS" className="h-10 w-10 object-contain" />
              <img src={ORG_LOGOS.IBTHM} alt="IBTHM" className="h-10 w-10 object-contain" />
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="font-heading font-bold text-primary text-base tracking-tight">ACIS | IBTHM</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-primary" : "text-slate-600 hover:text-primary"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button data-testid="header-dashboard-btn" variant="ghost" className="text-primary">
                    Dashboard
                  </Button>
                </Link>
                <Button data-testid="header-logout-btn" onClick={handleLogout} variant="outline">
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button data-testid="header-login-btn" variant="ghost" className="text-primary">
                    Sign in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button data-testid="header-apply-btn" className="bg-primary text-white hover:bg-[#113A64]">
                    Apply Now
                  </Button>
                </Link>
              </>
            )}
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-1 mt-8">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-md text-sm font-medium ${
                        isActive ? "bg-slate-100 text-primary" : "text-slate-700 hover:bg-slate-50"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <div className="border-t border-slate-200 my-3" />
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setOpen(false)}>
                      <Button className="w-full bg-primary text-white">Dashboard</Button>
                    </Link>
                    <Button variant="outline" className="w-full mt-2" onClick={handleLogout}>
                      Sign out
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)}>
                      <Button variant="outline" className="w-full">Sign in</Button>
                    </Link>
                    <Link to="/register" onClick={() => setOpen(false)}>
                      <Button className="w-full bg-primary text-white mt-2">Apply Now</Button>
                    </Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-slate-200 bg-slate-50" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={ORG_LOGOS.ACIS} alt="ACIS" className="h-10 w-10 object-contain" />
              <img src={ORG_LOGOS.IBTHM} alt="IBTHM" className="h-10 w-10 object-contain" />
            </div>
            <p className="text-sm text-slate-600 max-w-md leading-relaxed">
              The Accreditation Commission for Integrative Sciences (ACIS) accredits institutions
              and educational programs. The International Board of Trichology &amp; Holistic Medicine
              (IBTHM) board-certifies and licenses individual practitioners. Together they provide a
              unified public trust framework with independent verification.
            </p>
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-4">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <span>Secure · Verified · Independently Reviewed</span>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">For Professionals</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link className="hover:text-primary" to="/register">Apply for Credentials</Link></li>
              <li><Link className="hover:text-primary" to="/standards">Standards &amp; Requirements</Link></li>
              <li><Link className="hover:text-primary" to="/login">Practitioner Portal</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">For the Public</div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link className="hover:text-primary" to="/registry">Practitioner Registry</Link></li>
              <li><Link className="hover:text-primary" to="/verify">Verify a Credential</Link></li>
              <li><Link className="hover:text-primary" to="/contact">File a Complaint</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between text-xs text-slate-500">
            <span>© {new Date().getFullYear()} ACIS | IBTHM. All credentials independently verified.</span>
            <span className="hidden md:inline">Policy · Standards · Disciplinary Procedures</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
